import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { articlesAPI, categoriesAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';
import ConfirmModal from '../../components/common/ConfirmModal';
import Toast from '../../components/common/Toast';

const ArticleList = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [confirmModal, setConfirmModal] = useState({ show: false, articleId: null, action: null });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [currentPage, status, category]);

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getGuidanceCategories();
      if (response.data.success) {
        setCategories(response.data.data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const response = await articlesAPI.getAll({
        status,
        category,
        search,
        page: currentPage,
        limit: 20,
      });

      if (response.data.success) {
        setArticles(response.data.data.articles);
        setTotalPages(response.data.data.pagination.total_pages);
      }
    } catch (error) {
      console.error('Failed to fetch articles:', error);
      showToast('Failed to fetch articles', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchArticles();
  };

  const handlePublish = async (articleId) => {
    try {
      await articlesAPI.publish(articleId);
      showToast('Article published successfully', 'success');
      fetchArticles();
    } catch (error) {
      showToast('Failed to publish article', 'error');
    }
  };

  const handleUnpublish = async (articleId) => {
    try {
      await articlesAPI.unpublish(articleId);
      showToast('Article unpublished successfully', 'success');
      fetchArticles();
    } catch (error) {
      showToast('Failed to unpublish article', 'error');
    }
  };

  const handleDelete = async (articleId) => {
    try {
      await articlesAPI.delete(articleId);
      showToast('Article deleted successfully', 'success');
      fetchArticles();
    } catch (error) {
      showToast('Failed to delete article', 'error');
    }
    setConfirmModal({ show: false, articleId: null, action: null });
  };

  const handleRestore = async (articleId) => {
    try {
      await articlesAPI.restore(articleId);
      showToast('Article restored successfully', 'success');
      fetchArticles();
    } catch (error) {
      showToast('Failed to restore article', 'error');
    }
    setConfirmModal({ show: false, articleId: null, action: null });
  };

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
  };

  const executeConfirmedAction = () => {
    const { articleId, action } = confirmModal;
    if (action === 'delete') handleDelete(articleId);
    if (action === 'restore') handleRestore(articleId);
  };

  return (
    <div>
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />

      <ConfirmModal
        show={confirmModal.show}
        onClose={() => setConfirmModal({ show: false, articleId: null, action: null })}
        onConfirm={executeConfirmedAction}
        title={confirmModal.action === 'delete' ? 'Delete Article' : 'Restore Article'}
        message={
          confirmModal.action === 'delete'
            ? 'Are you sure you want to delete this article?'
            : 'Are you sure you want to restore this article?'
        }
        type={confirmModal.action === 'delete' ? 'danger' : 'warning'}
      />

      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="page-title">Guidance Articles</h2>
            <p className="page-subtitle">Manage educational content for users</p>
          </div>
          <Link to="/articles/new" className="btn btn-primary">
            <i className="bi bi-plus-circle me-2"></i>
            New Article
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="stat-card mb-4">
        <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Articles</option>
              <option value="published">Published</option>
              <option value="unpublished">Unpublished</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Category</label>
            <select
              className="form-select"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label">Search</label>
            <form onSubmit={handleSearch}>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search articles..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button className="btn btn-primary" type="submit">
                  <i className="bi bi-search"></i>
                </button>
              </div>
            </form>
          </div>

          <div className="col-md-2">
            <label className="form-label">&nbsp;</label>
            <button className="btn btn-outline-primary w-100" onClick={fetchArticles}>
              <i className="bi bi-arrow-clockwise me-2"></i>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Articles Table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="data-table">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Author</th>
                  <th>Views</th>
                  <th>Read Time</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {articles.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      <i className="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
                      <p className="text-muted mb-0">No articles found</p>
                    </td>
                  </tr>
                ) : (
                  articles.map((article) => (
                    <tr key={article.id}>
                      <td>
                        <div className="fw-semibold">{article.title}</div>
                        <small className="text-muted">{article.excerpt?.substring(0, 60)}...</small>
                      </td>
                      <td>
                        <span className="badge badge-purple">{article.category}</span>
                      </td>
                      <td>
                        <small>{article.author || 'Nisa Care Team'}</small>
                      </td>
                      <td>
                        <small>{article.views_count || 0}</small>
                      </td>
                      <td>
                        <small>{article.read_time} min</small>
                      </td>
                      <td>
                        {article.deleted_at ? (
                          <span className="badge badge-danger">Deleted</span>
                        ) : article.is_published ? (
                          <span className="badge badge-success">Published</span>
                        ) : (
                          <span className="badge badge-warning">Draft</span>
                        )}
                        {article.is_featured && (
                          <span className="badge bg-warning text-dark ms-1">Featured</span>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">
                          {new Date(article.created_at).toLocaleDateString()}
                        </small>
                      </td>
                      <td>
                        <div className="action-buttons">
                          {!article.deleted_at && (
                            <>
                              <Link
                                to={`/articles/edit/${article.id}`}
                                className="btn btn-sm btn-outline-primary"
                                title="Edit"
                              >
                                <i className="bi bi-pencil"></i>
                              </Link>
                              {article.is_published ? (
                                <button
                                  className="btn btn-sm btn-outline-warning"
                                  onClick={() => handleUnpublish(article.id)}
                                  title="Unpublish"
                                >
                                  <i className="bi bi-eye-slash"></i>
                                </button>
                              ) : (
                                <button
                                  className="btn btn-sm btn-outline-success"
                                  onClick={() => handlePublish(article.id)}
                                  title="Publish"
                                >
                                  <i className="bi bi-eye"></i>
                                </button>
                              )}
                            </>
                          )}
                          {article.deleted_at ? (
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => setConfirmModal({ show: true, articleId: article.id, action: 'restore' })}
                              title="Restore"
                            >
                              <i className="bi bi-arrow-counterclockwise"></i>
                            </button>
                          ) : (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => setConfirmModal({ show: true, articleId: article.id, action: 'delete' })}
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="p-3 border-top">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ArticleList;